import { create } from 'zustand'

import { ApiParseError, isCancelledError } from './errors'

interface ApiErrorNotification {
  id: number
  message: string
}

interface ApiErrorNotifications {
  sequence: number
  queue: ApiErrorNotification[]
  enqueue: (message: string) => void
  dismiss: (id: number) => void
}

const reportedErrors = new WeakSet<object>()

/** UI feedback only. Query and mutation state remain owned by TanStack Query. */
export const useApiErrorNotifications = create<ApiErrorNotifications>((set) => ({
  sequence: 0,
  queue: [],
  enqueue: (message) =>
    set((state) => {
      if (state.queue.some((entry) => entry.message === message)) return state
      const id = state.sequence + 1
      // Keep the visible toast and latest pending errors without an unbounded backlog.
      const pending = state.queue.length >= 5
        ? state.queue.slice(0, 1).concat(state.queue.slice(-3))
        : state.queue
      return { sequence: id, queue: [...pending, { id, message }] }
    }),
  dismiss: (id) => set((state) => ({ queue: state.queue.filter((entry) => entry.id !== id) })),
}))

export function wasApiErrorNotified(error: unknown) {
  return typeof error === 'object' && error !== null && reportedErrors.has(error)
}

export function apiErrorToastMessage(error: unknown) {
  if (error instanceof ApiParseError || (error instanceof Error && error.name === 'ZodError')) {
    return 'Data dari server belum dapat dibaca. Silakan coba lagi.'
  }
  if (error instanceof TypeError) return 'Koneksi bermasalah. Periksa jaringan kamu.'
  return error instanceof Error && error.message.trim()
    ? error.message.trim()
    : 'Permintaan belum berhasil. Silakan coba lagi.'
}

/** Safe for direct API actions and cache callbacks; reporting never swallows the error. */
export function reportApiError(error: unknown, { message }: { message?: string } = {}) {
  if (isCancelledError(error) || wasApiErrorNotified(error)) return
  if (typeof error === 'object' && error !== null) reportedErrors.add(error)
  useApiErrorNotifications.getState().enqueue(message?.trim() || apiErrorToastMessage(error))
}
